import { SEOHead } from "@/components/SEOHead";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight, Network, Truck, DollarSign, Settings } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";

const brokerageSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": "Brokerage",
  "provider": {
    "@type": "LocalBusiness",
    "name": "AITIM Group"
  },
  "areaServed": "US",
  "description": "Connecting You with Reliable Carriers for Seamless Shipping. Our brokerage services connect you with an extensive network of trusted carriers."
};

const Brokerage = () => {
  const { language } = useLanguage();
  const isRu = language === 'ru';
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background font-sans">
      <SEOHead 
        title="Connecting You with Reliable Carriers for Seamless Shipping"
        description="Finding reliable carriers can be challenging, especially when demand is high. Our brokerage services connect you with an extensive network of trusted carriers."
        canonical="/brokerage"
        schemaJson={brokerageSchema}
      />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden bg-navy-950">
          <div className="absolute inset-0 z-0">
            <img 
              src="https://assets.cdn.filesafe.space/gUCbLLrJwD5vtwXYoPEZ/media/69f0ff9b40534248c5a223af.webp" 
              alt="Brokerage" 
              className="w-full h-full object-cover object-center opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/80 to-transparent"></div>
          </div>

          <div className="container mx-auto px-4 relative z-10 text-center">
            <div className="max-w-4xl mx-auto">
              <div className="inline-block px-4 py-1.5 rounded-full bg-primary/20 text-primary font-semibold text-sm mb-6 border border-primary/30 tracking-wide uppercase">
                Brokerage
              </div>
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-8 leading-tight tracking-tight">
                Connecting You with Reliable Carriers for <span className="text-primary">Seamless Shipping</span>
              </h1>
              <Link to="/free-consultation">
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-white rounded-sm px-8 h-14 text-lg">
                  Get a Free Quote <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-24 bg-slate-50 relative">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {/* Feature 1 */}
              <div className="bg-white p-10 rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 hover:-translate-y-2 transition-all duration-300 group">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-300">
                  <Network className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-2xl font-bold text-navy-950 mb-4">Access to a Wide Network of Carriers</h3>
                <p className="text-slate-600 leading-relaxed">
                  Finding reliable carriers can be challenging, especially when demand is high. Our brokerage services connect you with an extensive network of trusted carriers, ensuring your freight moves quickly and efficiently. Let us handle the logistics while you focus on your core business.
                </p>
              </div>

              {/* Feature 2 */}
              <div className="bg-white p-10 rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 hover:-translate-y-2 transition-all duration-300 group">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-300">
                  <Truck className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-2xl font-bold text-navy-950 mb-4">End-to-End Logistics Support</h3>
                <p className="text-slate-600 leading-relaxed">
                  From pickup to delivery, our brokerage services manage every detail of your shipments, providing a seamless experience. We handle route planning, tracking, and communication with carriers to keep you updated, so you can have peace of mind knowing your freight is in good hands.
                </p>
              </div>

              {/* Feature 3 */}
              <div className="bg-white p-10 rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 hover:-translate-y-2 transition-all duration-300 group">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-300">
                  <DollarSign className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-2xl font-bold text-navy-950 mb-4">Competitive Rates for Cost Savings</h3>
                <p className="text-slate-600 leading-relaxed">
                  Negotiating rates can be time-consuming and complicated. With our industry expertise and established relationships, we secure competitive rates for your shipments, helping you save on transportation costs without sacrificing quality or reliability.
                </p>
              </div>

              {/* Feature 4 */}
              <div className="bg-white p-10 rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 hover:-translate-y-2 transition-all duration-300 group">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-300">
                  <Settings className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-2xl font-bold text-navy-950 mb-4">Flexible Solutions for Every Shipment</h3>
                <p className="text-slate-600 leading-relaxed">
                  Every shipment is different, and flexibility is key. Whether you need regular transport or a one-time solution, our brokerage services adapt to your unique needs, offering tailored solutions that keep your supply chain agile and responsive to changing demands.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Banner Section */}
        <section className="py-24 bg-navy-950 relative overflow-hidden">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="relative rounded-[2.5rem] overflow-hidden min-h-[320px] md:min-h-[400px] group shadow-2xl border border-white/5 bg-navy-900">
              {/* Background Image */}
              <div className="absolute inset-0 z-0">
                <img 
                  src="https://vibe.filesafe.space/1777392978847194987/assets/eb8ee3aa-631a-4271-9ab5-0652cd7aa6c3.png" 
                  alt="Brokerage" 
                  className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105 opacity-60" 
                />
                <div className="absolute inset-0 bg-gradient-to-r from-navy-950 via-navy-950/80 to-transparent"></div>
              </div>

              {/* Content */}
              <div className="relative z-10 h-full flex flex-col md:flex-row items-start md:items-center justify-between p-8 md:p-16 gap-8">
                <div className="max-w-2xl">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/20 border border-primary/30 text-primary font-bold tracking-widest uppercase text-[10px] mb-6">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></span>
                    Brokerage
                  </div>
                  <h3 className="text-4xl md:text-6xl font-black text-white tracking-tighter mb-4 leading-tight">
                    Expert Brokerage Services for Cost-Effective and Efficient Logistics
                  </h3>
                </div>

                <Link to="/free-consultation" className="shrink-0">
                  <Button 
                    size="lg"
                    className="group/btn relative overflow-hidden h-16 px-10 text-lg font-bold rounded-xl bg-primary text-white hover:bg-primary/90 shadow-[0_0_20px_rgba(59,130,246,0.5)] transition-all duration-300"
                  >
                    <span className="relative z-10 flex items-center gap-2">
                      Learn More about Brokerage
                      <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover/btn:translate-x-1" />
                    </span>
                    <div className="absolute top-0 -inset-full h-full w-1/2 z-5 block transform -skew-x-12 bg-gradient-to-r from-transparent to-white/10 opacity-40 group-hover/btn:animate-shine"></div>
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Stats & Hand Image Section */}
        <section className="py-24 bg-white overflow-hidden border-t border-slate-100">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16 max-w-[1920px] mx-auto">
            <div className="lg:w-1/2 relative group w-full flex justify-start overflow-hidden lg:overflow-visible">
              <div className="absolute inset-0 bg-primary/20 blur-3xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
              <img 
                src="https://static.wixstatic.com/media/5c8a3f_692872a1103a43c3b151fb190660505e~mv2.png/v1/fill/w_960,h_706,al_c,q_90,usm_0.66_1.00_0.01,enc_avif,quality_auto/5c8a3f_692872a1103a43c3b151fb190660505e~mv2.png" 
                alt="AITIM Capital Card" 
                className="w-full max-w-none lg:w-[110%] lg:-ml-12 drop-shadow-[0_20px_50px_rgba(0,0,0,0.15)] group-hover:-translate-y-4 group-hover:scale-105 transition-all duration-700 relative z-10"
              />
            </div>
            <div className="lg:w-1/2 px-4 lg:pr-12 xl:pr-24 w-full">
              <div className="max-w-xl mx-auto lg:mx-0">
                <div className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary font-bold text-xs mb-6 tracking-widest uppercase">
                  Why Choose Us
                </div>
                <h2 className="text-5xl md:text-7xl font-black text-navy-950 mb-16 tracking-tight">AITIM GROUP</h2>
                
                <div className="grid grid-cols-2 gap-x-4 sm:gap-x-8 gap-y-8 sm:gap-y-12">
                  <div className="group">
                    <div className="text-4xl md:text-6xl font-light text-navy-950 mb-4 group-hover:scale-110 origin-left transition-transform duration-300">10+</div>
                    <div className="h-0.5 bg-navy-950 w-full mb-4 relative overflow-hidden">
                      <div className="absolute top-0 left-0 h-full bg-primary w-0 group-hover:w-full transition-all duration-500"></div>
                    </div>
                    <div className="text-[10px] sm:text-sm font-medium tracking-widest text-navy-950 uppercase group-hover:text-primary transition-colors whitespace-pre-line">{isRu ? 'ЛЕТ ОПЫТА' : 'YEARS OF\nEXPERIENCE'}</div>
                  </div>
                  <div className="group">
                    <div className="text-4xl md:text-6xl font-light text-navy-950 mb-4 group-hover:scale-110 origin-left transition-transform duration-300">7000+</div>
                    <div className="h-0.5 bg-navy-950 w-full mb-4 relative overflow-hidden">
                      <div className="absolute top-0 left-0 h-full bg-primary w-0 group-hover:w-full transition-all duration-500"></div>
                    </div>
                    <div className="text-[10px] sm:text-sm font-medium tracking-widest text-navy-950 uppercase group-hover:text-primary transition-colors whitespace-pre-line">{isRu ? 'ГРУЗОВИКОВ В СЕТИ' : 'TRUCKS IN OUR\nNETWORK'}</div>
                  </div>
                  <div className="group">
                    <div className="text-4xl md:text-6xl font-light text-navy-950 mb-4 group-hover:scale-110 origin-left transition-transform duration-300">100%</div>
                    <div className="h-0.5 bg-navy-950 w-full mb-4 relative overflow-hidden">
                      <div className="absolute top-0 left-0 h-full bg-primary w-0 group-hover:w-full transition-all duration-500"></div>
                    </div>
                    <div className="text-[10px] sm:text-sm font-medium tracking-widest text-navy-950 uppercase group-hover:text-primary transition-colors whitespace-pre-line">{isRu ? 'УДОВЛЕТВОРЕННОСТЬ' : 'CUSTOMER\nSATISFACTION'}</div>
                  </div>
                  <div className="group">
                    <div className="text-4xl md:text-6xl font-light text-navy-950 mb-4 group-hover:scale-110 origin-left transition-transform duration-300">20+</div>
                    <div className="h-0.5 bg-navy-950 w-full mb-4 relative overflow-hidden">
                      <div className="absolute top-0 left-0 h-full bg-primary w-0 group-hover:w-full transition-all duration-500"></div>
                    </div>
                    <div className="text-[10px] sm:text-sm font-medium tracking-widest text-navy-950 uppercase group-hover:text-primary transition-colors whitespace-pre-line">{isRu ? 'НАГРАД И ПРИЗНАНИЙ' : 'AWARDS AND\nRECOGNITIONS'}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
};

export default Brokerage;
